import React, { useMemo } from 'react';
import { Link } from 'gatsby';
import { S21PromotionalCountdown } from '@milanlaser/mlcomponents';
import { useHasMounted } from '../../hooks/useHasMounted';
import { usePromoCountdown } from '../../hooks/usePromoCountdown';
import { formatPromoText } from './utils/bannerFormatting';
import { CountdownPlaceholder } from './utils/CountdownPlaceholder';

// The library passes LinkComponent an `href`, but Gatsby's Link reads `to`. It
// also always passes target="_self", which makes Link skip Gatsby's navigate(),
// so target/rel only go to plain anchors. Builder's linkTo may be absolute or
// protocol-relative (//host), which Link must not handle.
const isInternalPath = (href) => href?.startsWith('/') && !href.startsWith('//');

const CountdownLink = ({ href, target, rel, ...props }) =>
    isInternalPath(href) ? <Link to={href} {...props} /> : <a href={href} target={target} rel={rel} {...props} />;

// Rendered only where useCountdownActive() is true. The placeholder holds the
// banner's space until mount, since the timer itself can't be server-rendered.
export function PromoCountdown() {
    const { countdown } = usePromoCountdown();
    const hasMounted = useHasMounted();

    const promoText = useMemo(
        () => formatPromoText(countdown?.promoText, { supportsLineBreak: true }),
        [countdown?.promoText]
    );
    // react-countdown restarts whenever it receives a new Date instance.
    const endDate = useMemo(() => (countdown ? new Date(countdown.endMs) : null), [countdown]);

    if (!countdown) return null;

    const { linkTo, backgroundColor, textAlign } = countdown;

    if (!hasMounted) {
        return <CountdownPlaceholder promoText={promoText} backgroundColor={backgroundColor} textAlign={textAlign} />;
    }

    return (
        <S21PromotionalCountdown
            endDate={endDate}
            promoText={promoText}
            linkTo={linkTo}
            backgroundColor={backgroundColor}
            textAlign={textAlign}
            LinkComponent={CountdownLink}
        />
    );
}
